import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ImageIcon,
  CheckCircle2,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  Download,
  Play,
} from 'lucide-react';
import { FlagIcon } from '@/components/FlagIcon';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  getGXDownloadLinks,
  type GXAssetResponse,
  type GXMultiSearchResponse,
} from '@/api/gearExchange';
import type { Campaign } from '@/api/campaigns';
import { getEffectiveDimensions, calcAspectRatio, bestFitRatio } from './assetDimensions';

// ---------------------------------------------------------------------------
// Zone limits — passed in by each format component
// ---------------------------------------------------------------------------

export interface ZoneLimit {
  /** '1x1', '4x5', '9x16', or 'any' (any ratio counts toward the limit) */
  ratio: string;
  /** Maximum number of images for this zone */
  limit: number;
  /** How many images already exist in this zone (manually uploaded) */
  existing: number;
}

// ---------------------------------------------------------------------------
// Frontend matching logic
// ---------------------------------------------------------------------------

interface MatchResult {
  score: number;
  matches: string[];
  mismatches: string[];
  aspectRatio: string | null;
}

/**
 * Which zone an asset actually consumes.
 *
 * A 4:5 uses the format's 4x5 zone when it declares one (Meta Single/DC,
 * LinkedIn, the carousels). Formats without one still accept 4:5 into their
 * feed slot, so it falls back to 1x1 — the behaviour before 4:5 was split out.
 * 'any' zones swallow everything and are handled by the callers.
 */
export function resolveZoneRatio(assetRatio: string, zoneLimits: ZoneLimit[]): string {
  if (zoneLimits.some((z) => z.ratio === assetRatio)) return assetRatio;
  if (assetRatio === '4x5' && zoneLimits.some((z) => z.ratio === '1x1')) return '1x1';
  return assetRatio;
}

/** Extract resolution from filename (e.g., "..._1920x1080_..." → {w: 1920, h: 1080}).
 *  Used as fallback when GX returns 0x0 dimensions for videos.
 *  See assetDimensions.ts — extracted so it can be unit-tested. */

function extractCountryFromPath(folderPath: string): string | null {
  const parts = folderPath.split('/');
  if (parts.length >= 5) return parts[3];
  return null;
}

const LANG_NAME_TO_CODE: Record<string, string> = {
  german: 'DE',
  french: 'FR',
  italian: 'IT',
  spanish: 'ES',
  portuguese: 'PT',
  dutch: 'NL',
  english: 'EN',
  norwegian: 'NO',
  polish: 'PL',
  swedish: 'SE',
  danish: 'DK',
  finnish: 'FI',
  czech: 'CZ',
  hungarian: 'HU',
  romanian: 'RO',
  greek: 'EL',
  turkish: 'TR',
};

const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  NL: 'Netherlands',
  NO: 'Norway',
  IT: 'Italy',
  FR: 'France',
  DE: 'Germany',
  ES: 'Spain',
  PT: 'Portugal',
  PL: 'Poland',
  CH: 'Switzerland',
  AT: 'Austria',
  GB: 'Britain',
  GR: 'Greece',
  BE: 'Belgium',
  IE: 'Ireland',
  CZ: 'Czech Republic',
  HU: 'Hungary',
  RO: 'Romania',
  SE: 'Sweden',
  DK: 'Denmark',
  FI: 'Finland',
};

function filenameContains(filename: string, token: string): boolean {
  if (!token) return false;
  const upper = token.toUpperCase();
  const segments = filename.toUpperCase().split(/[_\-\s.()]+/);
  // Exact match OR segment starts with the token (e.g., "IMAGE01" matches "IMAGE")
  return segments.some((s) => s === upper || s.startsWith(upper));
}

function nameplateInFilename(filename: string, nameplate: string): boolean {
  if (!nameplate) return false;
  const upper = filename.toUpperCase();
  if (upper.includes(nameplate.toUpperCase().replace(/\s+/g, '-'))) return true;
  if (upper.includes(nameplate.toUpperCase().replace(/\s+/g, '_'))) return true;
  const words = nameplate.split(/[\s-]+/);
  return filenameContains(filename, words[0]);
}

function scoreAsset(asset: GXAssetResponse, campaign: Campaign): MatchResult {
  const matches: string[] = [];
  const mismatches: string[] = [];
  let score = 0;
  const filename = asset.filename;
  const assetIsVideo = isVideo(asset.file_type);

  // Use effective dimensions (falls back to filename for 0x0 videos)
  const { w, h } = getEffectiveDimensions(asset);
  const ratio = calcAspectRatio(w, h);

  // --- Size / aspect ratio (weight: 3) ---
  if (ratio && campaign.size) {
    const campaignSizes = campaign.size
      .toLowerCase()
      .split(/[&,]/)
      .map((s) => s.trim());
    if (campaignSizes.includes(ratio.toLowerCase())) {
      score += 3;
      matches.push(`Size: ${ratio} matches`);
    } else {
      mismatches.push(`Size: ${ratio} (campaign needs ${campaign.size})`);
    }
  }

  // --- Market (weight: 3) ---
  const folderCountry = extractCountryFromPath(asset.folder_path);
  if (folderCountry && campaign.market) {
    const expectedCountry = COUNTRY_CODE_TO_NAME[campaign.market.toUpperCase()];
    const campaignCountryName = expectedCountry || campaign.market;
    if (expectedCountry && folderCountry.toLowerCase() === expectedCountry.toLowerCase()) {
      score += 3;
      matches.push(`Market: ${folderCountry} matches ${campaign.market} (${campaignCountryName})`);
    } else if (filenameContains(filename, campaign.market)) {
      score += 2;
      matches.push(`Market: ${campaign.market} (${campaignCountryName}) found in filename`);
    } else {
      mismatches.push(
        `Market: folder is ${folderCountry} (campaign is ${campaign.market} — ${campaignCountryName})`,
      );
    }
  }

  // --- Nameplate (weight: 3) ---
  if (campaign.nameplate) {
    if (nameplateInFilename(filename, campaign.nameplate)) {
      score += 3;
      matches.push(`Nameplate: ${campaign.nameplate} found`);
    } else {
      mismatches.push(`Nameplate: ${campaign.nameplate} not found in filename`);
    }
  }

  // --- Language (weight: 2) ---
  if (campaign.language) {
    const langCode = LANG_NAME_TO_CODE[campaign.language.toLowerCase()];
    if (langCode && filenameContains(filename, langCode)) {
      score += 2;
      matches.push(`Language: ${langCode} (${campaign.language}) found`);
    } else if (langCode) {
      mismatches.push(`Language: ${langCode} (${campaign.language}) not found in filename`);
    }
  }

  // --- Format (weight: 1) ---
  if (campaign.format) {
    const formatLower = campaign.format.toLowerCase();
    const formatWords = formatLower.split(/[\s/]+/);
    const mainFormat = formatWords[0];

    // For video formats, check file type instead of searching filename
    if (formatLower.includes('video') && assetIsVideo) {
      score += 1;
      matches.push(`Format: video file (${asset.file_type})`);
    } else if (mainFormat && filenameContains(filename, mainFormat)) {
      score += 1;
      matches.push(`Format: "${mainFormat}" found in filename`);
    } else if (mainFormat) {
      mismatches.push(`Format: could not find "${mainFormat}" in filename`);
    }
  }

  // --- Video length (weight: 1, videos only) ---
  if (assetIsVideo && campaign.video_length) {
    // Extract all numbers from the campaign's video_length field
    const campaignDurations = (campaign.video_length.match(/\d+/g) || []).map(Number);
    // Extract duration from filename (e.g., "_15s_" → 15)
    const filenameDurationMatch = filename.match(/[_-](\d+)s[_\s.]/i);

    if (campaignDurations.length > 0 && filenameDurationMatch) {
      const fileDuration = parseInt(filenameDurationMatch[1]);
      if (campaignDurations.includes(fileDuration)) {
        score += 1;
        matches.push(`Video length: ${fileDuration}s matches`);
      } else {
        mismatches.push(`Video length: ${fileDuration}s (campaign needs ${campaign.video_length})`);
      }
    }
  }

  return { score, matches, mismatches, aspectRatio: ratio };
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export interface GXImportedFile {
  file: File;
  ratio: '1x1' | '4x5' | '9x16';
  filename: string;
}

interface SlotFilter {
  acceptTypes?: string[];
  maxFileSize?: number;
  dimensions?: string;
  acceptedRatios?: number[];
}

interface GearExchangeModalProps {
  data: GXMultiSearchResponse;
  campaign: Campaign;
  campaignId: string;
  formatLabel: string;
  zoneLimits: ZoneLimit[];
  onClose: () => void;
  onImport: (files: GXImportedFile[]) => void;
  slotFilter?: SlotFilter;
  /** Map of filename → variant numbers that already use that image (across all variants) */
  usedInVariants?: Map<string, number[]>;
}

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv']);

function isVideo(fileType: string): boolean {
  return VIDEO_EXTENSIONS.has(fileType.toLowerCase());
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Asset preview lightbox
// ---------------------------------------------------------------------------

function AssetPreviewLightbox({
  asset,
  campaignId,
  onClose,
}: {
  asset: GXAssetResponse;
  campaignId: string;
  onClose: () => void;
}) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const isVideoAsset = isVideo(asset.file_type);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  async function handlePlayVideo() {
    setVideoLoading(true);
    try {
      const { getGXDownloadLinks } = await import('@/api/gearExchange');
      const { links } = await getGXDownloadLinks(campaignId, [asset.list_item_id]);
      const link = links[0];
      if (link?.download_url) {
        setVideoUrl(link.download_url);
      }
    } catch {
      // fallback — can't play
    } finally {
      setVideoLoading(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 p-1.5 rounded-lg text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {isVideoAsset && videoUrl ? (
          <video src={videoUrl} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg" />
        ) : isVideoAsset ? (
          <div className="w-[600px] h-[400px] bg-slate-900 rounded-lg flex flex-col items-center justify-center">
            <button
              onClick={handlePlayVideo}
              disabled={videoLoading}
              className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              {videoLoading ? (
                <Loader2 className="w-7 h-7 text-white animate-spin" />
              ) : (
                <Play className="w-7 h-7 text-white fill-white ml-1" />
              )}
            </button>
            <p className="text-xs text-white/30 mt-4">
              {videoLoading ? 'Downloading video…' : 'Click to download and play'}
            </p>
          </div>
        ) : (
          <img
            src={asset.preview_url || asset.thumbnail_url}
            alt={asset.filename}
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Asset card
// ---------------------------------------------------------------------------

function AssetCard({
  asset,
  matchResult,
  isSelected,
  isDisabled,
  disabledReason,
  onToggle,
  showReasons,
  showFordshareTag,
  onPreview,
  usedInVariants,
}: {
  asset: GXAssetResponse;
  matchResult: MatchResult;
  isSelected: boolean;
  isDisabled: boolean;
  disabledReason: string | null;
  onToggle: () => void;
  showReasons: boolean;
  showFordshareTag?: boolean;
  onPreview?: () => void;
  usedInVariants?: number[];
}) {
  // Max score: 12 base + 1 for video length (only for videos with campaign video_length)
  const maxScore = isVideo(asset.file_type) ? 13 : 12;
  const scorePercent = Math.min(100, Math.round((matchResult.score / maxScore) * 100));
  const hasReasons = matchResult.matches.length > 0 || matchResult.mismatches.length > 0;

  const card = (
    <div
      onClick={() => !isDisabled && onToggle()}
      role="button"
      tabIndex={0}
      className={`
        relative rounded-xl border overflow-hidden text-left transition-all duration-200 group
        ${
          isSelected
            ? 'border-[#0014cc] ring-2 ring-[#0014cc]/20 shadow-md cursor-pointer'
            : isDisabled
              ? 'border-slate-200 opacity-40'
              : 'border-slate-200 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 cursor-pointer'
        }
      `}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center overflow-hidden relative">
        {asset.thumbnail_url ? (
          <img
            src={asset.thumbnail_url}
            alt={asset.filename}
            className={`w-full h-full object-contain transition-transform duration-200 ${!isDisabled && !isSelected ? 'group-hover:scale-105' : ''}`}
          />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-300" />
        )}

        {/* Video indicator */}
        {isVideo(asset.file_type) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}

        {/* Fordshare ID tag — shown in "All" view with multiple IDs */}
        {showFordshareTag && (
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm rounded text-[9px] font-medium text-white tabular-nums">
            # {asset.fordshare_id}
          </div>
        )}

        {/* Hover buttons — download (bottom-left) + preview (bottom-right) */}
        {asset.thumbnail_url && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const resp = await fetch(asset.preview_url || asset.thumbnail_url);
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = asset.filename;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      window.open(asset.preview_url || asset.thumbnail_url, '_blank');
                    }
                  }}
                  className="absolute bottom-2 left-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                >
                  <Download className="w-3.5 h-3.5 text-slate-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview?.();
                  }}
                  className="absolute bottom-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                >
                  <Eye className="w-3.5 h-3.5 text-slate-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Preview</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2.5 right-2.5 w-6 h-6 bg-[#0014cc] rounded-full flex items-center justify-center shadow-sm">
          <CheckCircle2 className="w-4 h-4 text-white" />
        </div>
      )}

      {/* Info bar */}
      <div className="px-3 py-2.5 bg-white border-t border-slate-100">
        <p className="text-[10px] text-slate-600 leading-snug break-all">{asset.filename}</p>

        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
            <span className="font-medium text-slate-500">
              {matchResult.aspectRatio ||
                (isVideo(asset.file_type) ? 'Video' : `${asset.width}×${asset.height}`)}
            </span>
            <span>·</span>
            <span>{formatFileSize(asset.file_size)}</span>
            <span>·</span>
            <span className="uppercase">{asset.file_type}</span>
          </div>
          <span
            className={`
            text-[9px] font-bold px-1.5 py-0.5 rounded-full
            ${
              scorePercent >= 80
                ? 'bg-emerald-100 text-emerald-700'
                : scorePercent >= 50
                  ? 'bg-slate-100 text-slate-500'
                  : 'bg-slate-50 text-slate-400'
            }
          `}
          >
            {scorePercent}%
          </span>
        </div>

        {/* Used-in-variant badge */}
        {usedInVariants && usedInVariants.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#0014cc]/5 border border-[#0014cc]/20 rounded text-[9px] font-medium text-[#0014cc]">
                <CheckCircle2 className="w-2.5 h-2.5" />
                Used in {usedInVariants.map((v) => `Variant ${v}`).join(', ')}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              This image is already used in {usedInVariants.map((v) => `Variant ${v}`).join(', ')}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Expandable match/mismatch reasons */}
        {hasReasons && showReasons && (
          <div className="mt-2 pt-2 border-t border-slate-50 space-y-0.5">
            {matchResult.matches.map((reason, i) => (
              <p key={`m-${i}`} className="text-[10px] text-emerald-600 flex items-center gap-1">
                <Check className="w-2.5 h-2.5 flex-shrink-0" />
                {reason}
              </p>
            ))}
            {matchResult.mismatches.map((reason, i) => (
              <p key={`mm-${i}`} className="text-[10px] text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />
                {reason}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isDisabled && disabledReason) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent className="max-w-56">{disabledReason}</TooltipContent>
      </Tooltip>
    );
  }

  return card;
}

// ---------------------------------------------------------------------------
// Campaign detail sidebar
// ---------------------------------------------------------------------------

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase mb-0.5">
        {label}
      </p>
      {children}
    </div>
  );
}

function DetailText({ value }: { value: string | null | undefined }) {
  return (
    <p className="text-xs text-slate-600">{value || <span className="text-slate-300">—</span>}</p>
  );
}

// ---------------------------------------------------------------------------
// Zone counter badge
// ---------------------------------------------------------------------------

function ZoneCounter({
  zone,
  selectedCount,
  formatLabel,
}: {
  zone: ZoneLimit;
  selectedCount: number;
  formatLabel: string;
}) {
  const available = zone.limit - zone.existing;
  const isFull = selectedCount > 0 && selectedCount >= available;
  const isFilledByExisting = zone.existing >= zone.limit;
  const ratioLabel = zone.ratio === 'any' ? 'Asset' : `${zone.ratio} asset`;

  // Build a natural language tooltip
  const sizeDesc = zone.ratio === 'any' ? 'asset' : `${zone.ratio} asset`;
  let tooltipText = `${formatLabel} allows up to ${zone.limit} ${sizeDesc}${zone.limit !== 1 ? 's' : ''}.`;
  if (zone.existing > 0) {
    tooltipText += ` You already have ${zone.existing} uploaded in the asset preview — you'll need to remove ${zone.existing === 1 ? 'it' : 'some'} there before importing more here.`;
  }
  if (available === 0) {
    tooltipText += ' All slots are currently filled.';
  } else if (selectedCount > 0) {
    tooltipText += ` You've selected ${selectedCount} of ${available} available slot${available !== 1 ? 's' : ''}.`;
  } else {
    tooltipText += ` You have ${available} slot${available !== 1 ? 's' : ''} available to import into.`;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`
          inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] transition-colors cursor-help
          ${
            isFull || isFilledByExisting
              ? 'bg-emerald-50 border border-emerald-200'
              : 'bg-slate-50 border border-slate-200'
          }
        `}
        >
          <span
            className={`font-semibold ${isFull || isFilledByExisting ? 'text-emerald-700' : 'text-slate-600'}`}
          >
            {ratioLabel}:
          </span>
          <span className={isFull || isFilledByExisting ? 'text-emerald-600' : 'text-slate-500'}>
            {isFilledByExisting
              ? `${zone.existing} of ${zone.limit} filled`
              : `${selectedCount} of ${available} slot${available !== 1 ? 's' : ''}`}
          </span>
          {zone.existing > 0 && (
            <span
              className={`text-[9px] ${isFull || isFilledByExisting ? 'text-emerald-400' : 'text-slate-400'}`}
            >
              +{zone.existing} existing
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export function GearExchangeModal({
  data,
  campaign,
  campaignId,
  formatLabel,
  zoneLimits,
  onClose,
  onImport,
  slotFilter,
  usedInVariants,
}: GearExchangeModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showReasons, setShowReasons] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [ratioFilter, setRatioFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [hideUnavailable, setHideUnavailable] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<GXAssetResponse | null>(null);

  const allAssets = useMemo(() => {
    return data.results.flatMap((r) => r.assets);
  }, [data.results]);

  const hasTabs = data.results.length > 1;

  const rankedAssets = useMemo(() => {
    const sourceAssets =
      activeTab === 'all'
        ? allAssets
        : (data.results.find((r) => r.fordshare_id === activeTab)?.assets ?? []);

    return sourceAssets
      .map((asset) => {
        const { w, h } = getEffectiveDimensions(asset);
        const matchResult = scoreAsset(asset, campaign);
        return {
          asset,
          matchResult,
          assetRatio: bestFitRatio(w, h),
          actualRatio: matchResult.aspectRatio,
        };
      })
      .sort(
        (a, b) =>
          b.matchResult.score - a.matchResult.score ||
          a.asset.filename.localeCompare(b.asset.filename),
      );
  }, [allAssets, data.results, activeTab, campaign]);

  // Unique aspect ratios for filter dropdown
  const availableRatios = useMemo(() => {
    const ratios = new Set(rankedAssets.map((a) => a.matchResult.aspectRatio || 'other'));
    return Array.from(ratios).sort();
  }, [rankedAssets]);

  // Apply ratio filter
  const filteredAssets = useMemo(() => {
    if (ratioFilter === 'all') return rankedAssets;
    return rankedAssets.filter((a) => (a.matchResult.aspectRatio || 'other') === ratioFilter);
  }, [rankedAssets, ratioFilter]);

  const allRankedAssets = useMemo(() => {
    return allAssets.map((asset) => {
      const { w, h } = getEffectiveDimensions(asset);
      return { asset, assetRatio: bestFitRatio(w, h) };
    });
  }, [allAssets]);

  const selectedCountByRatio = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const { asset, assetRatio } of allRankedAssets) {
      if (selectedIds.has(asset.list_item_id)) {
        // Count against the zone it will actually fill, so a 4:5 heading for a
        // 1x1 zone consumes 1x1 capacity rather than an absent 4x5 budget.
        const zoneRatio = resolveZoneRatio(assetRatio, zoneLimits);
        counts[zoneRatio] = (counts[zoneRatio] || 0) + 1;
        counts['any'] = (counts['any'] || 0) + 1;
      }
    }
    return counts;
  }, [selectedIds, allRankedAssets, zoneLimits]);

  function isAssetDisabled(
    assetRatio: '1x1' | '4x5' | '9x16',
    actualRatio: string | null,
    listItemId: string,
    asset?: GXAssetResponse,
  ): { disabled: boolean; reason: string | null } {
    if (selectedIds.has(listItemId)) return { disabled: false, reason: null };

    // Slot-specific file type check
    if (slotFilter?.acceptTypes && asset) {
      const ext = asset.file_type.toLowerCase();
      const extToMime: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        tif: 'image/tiff',
        tiff: 'image/tiff',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
      };
      const mime = extToMime[ext] ?? `image/${ext}`;
      if (!slotFilter.acceptTypes.includes(mime)) {
        const allowed = slotFilter.acceptTypes
          .map((t) => t.split('/')[1]?.toUpperCase())
          .join(', ');
        return { disabled: true, reason: `Only ${allowed} files accepted for this slot.` };
      }
    }

    // Slot-specific file size check
    if (slotFilter?.maxFileSize && asset && asset.file_size > slotFilter.maxFileSize) {
      const maxKB = slotFilter.maxFileSize / 1024;
      const sizeLabel = maxKB >= 1024 ? `${Math.round(maxKB / 1024)}MB` : `${Math.round(maxKB)}KB`;
      const fileKB = asset.file_size / 1024;
      const fileLabel =
        fileKB >= 1024 ? `${(fileKB / 1024).toFixed(1)}MB` : `${Math.round(fileKB)}KB`;
      return {
        disabled: true,
        reason: `File is ${fileLabel} — maximum ${sizeLabel} for this slot.`,
      };
    }

    // Slot-specific aspect ratio check — orientation-based
    if (slotFilter?.acceptedRatios && slotFilter.acceptedRatios.length > 0 && asset) {
      const { w, h } = getEffectiveDimensions(asset);
      if (w > 0 && h > 0) {
        const ar = w / h;
        const matches = slotFilter.acceptedRatios.some((r) => {
          if (Math.abs(r - 1) < 0.05) return ar >= 0.85 && ar <= 1.15; // square
          if (r < 0.85) return ar < 0.85; // portrait
          if (r >= 3) return Math.abs(ar - r) <= r * 0.3; // wide logo/banner — tight
          if (r > 1.2) return ar > 1.2; // landscape
          return Math.abs(ar - r) <= r * 0.3; // fallback
        });
        if (!matches) {
          const ratioLabel = (r: number) => {
            if (Math.abs(r - 1) < 0.05) return '1:1 (square)';
            if (Math.abs(r - 1.91) < 0.2) return '1.91:1 (landscape)';
            if (Math.abs(r - 4) < 0.5) return '4:1 (wide landscape)';
            if (Math.abs(r - 0.8) < 0.1) return '4:5 (portrait)';
            if (Math.abs(r - 0.5) < 0.1) return '1:2 (tall portrait)';
            if (Math.abs(r - 5) < 0.5) return '5:1 (banner)';
            return `${r}:1`;
          };
          const expected = slotFilter.acceptedRatios.map(ratioLabel).join(', ');
          return {
            disabled: true,
            reason: `Aspect ratio doesn't match — this slot requires ${expected}.`,
          };
        }
      }
    }

    // Check actual ratio against zones (catches 16:9, 4:3 etc. that no format supports).
    // resolveZoneRatio lets a 4:5 through on formats whose feed zone is 1x1 only.
    const ratioToCheck = actualRatio ?? assetRatio;
    const resolved = resolveZoneRatio(ratioToCheck, zoneLimits);
    const hasMatchingZone = zoneLimits.some((z) => z.ratio === 'any' || z.ratio === resolved);
    if (!hasMatchingZone) {
      const acceptedRatios = zoneLimits.map((z) => z.ratio).join(', ');
      return {
        disabled: true,
        reason: `This format only accepts ${acceptedRatios} media.`,
      };
    }

    for (const zone of zoneLimits) {
      const matchesZone =
        zone.ratio === 'any' || zone.ratio === resolveZoneRatio(assetRatio, zoneLimits);
      if (!matchesZone) continue;

      const available = zone.limit - zone.existing;
      const selected =
        zone.ratio === 'any'
          ? selectedCountByRatio['any'] || 0
          : selectedCountByRatio[zone.ratio] || 0;

      if (selected >= available) {
        const ratioLabel = zone.ratio === 'any' ? 'asset' : zone.ratio;
        return {
          disabled: true,
          reason:
            zone.existing > 0
              ? `${ratioLabel} limit reached (${zone.existing} pre-existing). Unselect an asset, or delete existing ones and save to free up slots.`
              : `${ratioLabel} limit reached. Unselect an asset to choose a different one.`,
        };
      }
    }

    return { disabled: false, reason: null };
  }

  function toggleSelection(listItemId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listItemId)) {
        next.delete(listItemId);
      } else {
        next.add(listItemId);
      }
      return next;
    });
  }

  async function handleImport() {
    const selectedAssets = allAssets.filter((a) => selectedIds.has(a.list_item_id));
    if (selectedAssets.length === 0) return;

    setImporting(true);
    setImportError(null);

    try {
      const { links } = await getGXDownloadLinks(
        campaignId,
        selectedAssets.map((a) => a.list_item_id),
      );

      const extToMime: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        tif: 'image/tiff',
        tiff: 'image/tiff',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        pdf: 'application/pdf',
      };

      const downloadResults = await Promise.allSettled(
        selectedAssets.map(async (asset) => {
          const link = links.find((l) => l.list_item_id === asset.list_item_id);
          if (!link?.download_url) return null;

          const response = await fetch(link.download_url);
          if (!response.ok) return null;

          const blob = await response.blob();
          const ext = asset.file_type.toLowerCase();
          const contentType = extToMime[ext] || blob.type || `image/${ext}`;
          const file = new File([blob], asset.filename, { type: contentType });
          const { w: ew, h: eh } = getEffectiveDimensions(asset);
          return { file, ratio: bestFitRatio(ew, eh), filename: asset.filename } as GXImportedFile;
        }),
      );

      const importedFiles = downloadResults
        .filter((r): r is PromiseFulfilledResult<GXImportedFile | null> => r.status === 'fulfilled')
        .map((r) => r.value)
        .filter((f): f is GXImportedFile => f !== null);

      if (importedFiles.length === 0) {
        setImportError('Could not download any assets. Try again.');
        return;
      }

      onImport(importedFiles);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setImportError(msg);
    } finally {
      setImporting(false);
    }
  }

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
      <div className="w-[1080px] h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Import from Gear Exchange</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {formatLabel} · {data.total_assets} asset{data.total_assets !== 1 ? 's' : ''} found
              {data.fordshare_ids.length === 1
                ? ` for FordShare ID ${data.fordshare_ids[0]}`
                : ` across ${data.fordshare_ids.length} FordShare IDs`}
              {slotFilter?.dimensions && (
                <>
                  {' · '}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="underline underline-offset-2 decoration-slate-300 cursor-default">
                        Specs
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-80 p-0">
                      <table className="text-xs">
                        <tbody>
                          {slotFilter.dimensions.split('·').map((part, i) => {
                            const trimmed = part.trim();
                            if (!trimmed) return null;
                            const dashIdx = trimmed.indexOf('—');
                            if (dashIdx > 0) {
                              return (
                                <tr key={i} className="border-b border-white/5 last:border-0">
                                  <td className="py-1 px-3 font-medium text-white whitespace-nowrap">
                                    {trimmed.slice(0, dashIdx).trim()}
                                  </td>
                                  <td className="py-1 px-3 text-slate-300">
                                    {trimmed.slice(dashIdx + 1).trim()}
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={i} className="border-b border-white/5 last:border-0">
                                <td colSpan={2} className="py-1.5 px-3 text-slate-300">
                                  {trimmed}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {availableRatios.length > 1 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <select
                    value={ratioFilter}
                    onChange={(e) => setRatioFilter(e.target.value)}
                    className="px-2.5 py-1.5 text-[11px] font-medium text-slate-500 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <option value="all">All sizes</option>
                    {availableRatios.map((r) => (
                      <option key={r} value={r}>
                        {r === 'other' ? 'Other' : r}
                      </option>
                    ))}
                  </select>
                </TooltipTrigger>
                <TooltipContent>Filter assets by aspect ratio</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowReasons((prev) => !prev)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {showReasons ? (
                    <EyeOff className="w-3.5 h-3.5" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                  {showReasons ? 'Hide' : 'Show'} match details
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Show how well each asset matches this campaign's fields
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setHideUnavailable((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${
                    hideUnavailable
                      ? 'text-[#0014cc] border-[#0014cc]/30 bg-[#0014cc]/5'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                  {hideUnavailable ? 'Show all' : 'Available only'}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {hideUnavailable
                  ? 'Show all assets including unavailable ones'
                  : "Hide assets that can't be imported into this slot"}
              </TooltipContent>
            </Tooltip>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Left: Campaign details */}
          <div className="w-[230px] flex-shrink-0 border-r border-slate-100 overflow-y-auto px-5 py-5 bg-slate-50/50">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
              Campaign Details
            </p>
            <div className="space-y-3.5">
              <DetailRow label="Platform">
                <PlatformIcon platform={campaign.platform} />
              </DetailRow>
              <DetailRow label="Market">
                {campaign.market ? (
                  <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs bg-white text-slate-600 rounded-md border border-slate-200">
                    <FlagIcon countryCode={campaign.market} className="w-3.5 h-auto rounded-sm" />
                    {campaign.market}
                  </span>
                ) : (
                  <DetailText value={null} />
                )}
              </DetailRow>
              <DetailRow label="Nameplate">
                <DetailText value={campaign.nameplate} />
              </DetailRow>
              <DetailRow label="Language">
                <DetailText value={campaign.language} />
              </DetailRow>
              <DetailRow label="Format">
                <DetailText value={campaign.format} />
              </DetailRow>
              <DetailRow label="Size">
                <DetailText value={campaign.size} />
              </DetailRow>
              <DetailRow label="Placement">
                <DetailText value={campaign.placement} />
              </DetailRow>
              <DetailRow label="Quarter">
                <DetailText value={campaign.quarter} />
              </DetailRow>
              {campaign.video_length && (
                <DetailRow label="Video Length">
                  <DetailText value={campaign.video_length} />
                </DetailRow>
              )}
            </div>
            <div className="mt-5 pt-3 border-t border-slate-200/60">
              <p className="text-[10px] text-slate-400 leading-relaxed">
                Assets are ranked by how well they appear to match these fields. This is a
                best-effort suggestion — not guaranteed to be accurate.
              </p>
            </div>
          </div>

          {/* Right: Zone header + image grid */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Tabs + zone counters bar */}
            <div className="flex-shrink-0 px-5 border-b border-slate-100 bg-white flex items-center justify-between">
              <div
                className="flex items-center gap-1 overflow-x-auto pb-0.5"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
              >
                {hasTabs && (
                  <>
                    <button
                      onClick={() => setActiveTab('all')}
                      className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === 'all'
                          ? 'border-[#0014cc] text-[#0014cc]'
                          : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      All ({data.total_assets})
                    </button>
                    {data.results.map((r) => (
                      <button
                        key={r.fordshare_id}
                        onClick={() => setActiveTab(r.fordshare_id)}
                        className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors tabular-nums whitespace-nowrap ${
                          activeTab === r.fordshare_id
                            ? 'border-[#0014cc] text-[#0014cc]'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        # {r.fordshare_id} ({r.total_assets})
                      </button>
                    ))}
                  </>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 py-2.5 ml-4">
                {zoneLimits.map((zone) => (
                  <ZoneCounter
                    key={zone.ratio}
                    zone={zone}
                    formatLabel={formatLabel}
                    selectedCount={
                      zone.ratio === 'any'
                        ? selectedCountByRatio['any'] || 0
                        : selectedCountByRatio[zone.ratio] || 0
                    }
                  />
                ))}
              </div>
            </div>

            {/* Image grid */}
            <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50/30">
              {data.total_assets === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <ImageIcon className="w-10 h-10 text-slate-200" />
                  <p className="text-sm text-slate-400">No assets found for this Fordshare ID</p>
                  <div className="flex items-start gap-2 mt-2 px-6 py-3 bg-amber-50 border border-amber-200 rounded-lg max-w-md">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Assets linked to FordShare IDs created before 26/03/2026 are not accessible
                      via the API. Please upload these assets manually.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {filteredAssets.map(({ asset, matchResult, assetRatio, actualRatio }) => {
                    const { disabled, reason } = isAssetDisabled(
                      assetRatio,
                      actualRatio,
                      asset.list_item_id,
                      asset,
                    );
                    if (hideUnavailable && disabled) return null;
                    return (
                      <AssetCard
                        key={asset.list_item_id}
                        asset={asset}
                        matchResult={matchResult}
                        isSelected={selectedIds.has(asset.list_item_id)}
                        isDisabled={disabled}
                        disabledReason={reason}
                        onToggle={() => toggleSelection(asset.list_item_id)}
                        showReasons={showReasons}
                        showFordshareTag={hasTabs && activeTab === 'all'}
                        onPreview={() => setPreviewAsset(asset)}
                        usedInVariants={usedInVariants?.get(asset.filename)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {data.total_assets > 0 && (
          <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              {importError && <p className="text-xs text-red-500">{importError}</p>}
              <p className="text-xs text-slate-400">
                {importing
                  ? 'Downloading assets…'
                  : selectedIds.size > 0
                    ? `${selectedIds.size} asset${selectedIds.size !== 1 ? 's' : ''} selected`
                    : 'Click assets to select them'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={importing}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <button
                      disabled={selectedIds.size === 0 || importing}
                      onClick={handleImport}
                      className={`
                        flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all
                        ${
                          selectedIds.size > 0 && !importing
                            ? 'text-white bg-[#0014cc] hover:bg-[#0014cc]/90 shadow-sm'
                            : 'text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed'
                        }
                      `}
                    >
                      {importing && <Loader2 className="w-4 h-4 animate-spin" />}
                      {importing
                        ? 'Importing…'
                        : `Import ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
                    </button>
                  </span>
                </TooltipTrigger>
                {selectedIds.size === 0 && !importing && (
                  <TooltipContent>Select one or more assets to import</TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        )}

        {previewAsset && (
          <AssetPreviewLightbox
            asset={previewAsset}
            campaignId={campaignId}
            onClose={() => setPreviewAsset(null)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
